import { cloneElement, toChildArray } from 'preact';
import type { ComponentChildren, VNode } from 'preact';
import { route } from 'preact-router';
import { useEffect } from 'preact/hooks';
import { useAuth } from '../../context/AuthContext.js';
import { rememberReturnPath } from '../../services/signInReturnPath.js';
import { CampusRequired } from './CampusRequired.js';
import { IdentityNumberRequired } from './IdentityNumberRequired.js';
import { PhoneRequired } from './PhoneRequired.js';
import { Spinner } from './Spinner.js';

interface AuthGuardProps {
  children: ComponentChildren;
  path?: string;
  // biome-ignore lint/suspicious/noExplicitAny: route params from preact-router vary by path
  [key: string]: any;
}

export function AuthGuard({ children, path: _path, ...routeProps }: AuthGuardProps) {
  const { isAuthenticated, loading, user } = useAuth();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // 079 D1: the only chokepoint where a protected destination becomes a trip to sign-in, so
      // the only place the visitor's intent can be captured — and capturing here covers all 11
      // wrapped destinations without special-casing any of them (FR-010).
      //
      // 🔴 Before the navigation, not after: route() is synchronous, so a later write would race
      // the sign-in screen's own mount. And only in this branch: the "back to sign in" links and
      // the terminal-failure redirects must not capture, or arrival loops (FR-009, research R1).
      rememberReturnPath(window.location.pathname + window.location.search);
      route('/account/login');
    }
  }, [loading, isAuthenticated]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  /**
   * 155 (#19): registration by email requires a phone number; Google sign-up creates the account
   * without one, so those customers have none and nothing ever asked. Enforced HERE because this
   * is already the single chokepoint for every protected destination — one place rather than
   * eleven, and existing accounts are covered because the condition is the missing field rather
   * than how the account was made.
   *
   * Deliberately after the authentication check and before the child renders: a customer who is
   * not signed in belongs at sign-in, not here, and the protected screen must not render behind
   * this and then be replaced.
   */
  if (user && !user.phone) return <PhoneRequired />;

  /**
   * 330 (US2, FR-003): every customer chooses their campus once, and sees only its cars. The same
   * chokepoint and the same kind of condition as the phone step: the missing field, so new, Google and
   * existing accounts are all asked, with nothing run against the database. After the phone, so a
   * Google account without one gives its phone first.
   */
  if (user && !user.campus_location_id) return <CampusRequired />;

  /**
   * 366 (#1223, US2): the MyKad or passport number, once. The server decides who is asked (a customer with none; staff
   * never), so the condition is its flag, not a role read here. After the campus, so nothing earlier moves.
   */
  if (user?.identity_number_required) return <IdentityNumberRequired />;

  // Forward route params (e.g. id) injected by preact-router into the child
  const [child] = toChildArray(children);
  if (child && typeof child === 'object') {
    return cloneElement(child as VNode, routeProps);
  }

  return <>{children}</>;
}
